import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Cake, Heart, Loader2 } from "lucide-react";
import { DASHBOARD } from "@/constants/testIds";

const MONTHS_ES = [
  "Ene", "Feb", "Mar", "Abr", "May", "Jun",
  "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
];

const fmtDate = (m, d) => `${String(d).padStart(2, "0")} ${MONTHS_ES[m - 1]}`;

const relLabel = (days) => {
  if (days === 0) return "Hoy";
  if (days === 1) return "Mañana";
  return `En ${days} días`;
};

const relClass = (days) => {
  if (days === 0) return "bg-[#c8a04a] text-[#1c1a14]";
  if (days <= 3) return "bg-[#e7c879] text-[#1c1a14]";
  return "bg-[#f3ecd6] text-[#7a5b0f]";
};

export default function UpcomingEvents() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/dashboard/upcoming-events", { params: { days: 30 } });
        if (alive) setData(res.data);
      } catch (e) {
        if (alive) setError("No se pudieron cargar los eventos próximos");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const birthdays = data?.birthdays || [];
  const anniversaries = data?.anniversaries || [];

  return (
    <div
      data-testid={DASHBOARD.upcomingWidget}
      className="bg-white border border-[#d9cfb8] p-6"
      style={{ borderTop: "3px solid #c8a04a" }}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#8b7e5e]">
            Próximos 30 días
          </div>
          <h3 className="font-serif text-xl text-[#1c1a14]">
            Cumpleaños y aniversarios
          </h3>
        </div>
        {loading && <Loader2 size={16} className="animate-spin text-[#8b7e5e]" />}
      </div>

      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Birthdays */}
        <div>
          <div className="flex items-center gap-2 mb-3 text-[#1c2a3a]">
            <Cake size={16} className="text-[#c8a04a]" />
            <span className="text-xs uppercase tracking-widest font-semibold">
              Cumpleaños
            </span>
            <span className="ml-auto text-xs text-[#8b7e5e]">
              {birthdays.length}
            </span>
          </div>
          <ul
            data-testid={DASHBOARD.upcomingBirthdaysList}
            className="space-y-1 max-h-64 overflow-y-auto pr-1"
          >
            {birthdays.length === 0 && !loading && (
              <li className="text-xs text-[#8b7e5e] italic py-2">
                Sin cumpleaños próximos
              </li>
            )}
            {birthdays.map((b) => (
              <li
                key={`b-${b.id}`}
                data-testid={DASHBOARD.upcomingBirthdayItem(b.id)}
                className="flex items-center gap-3 px-3 py-2 border border-[#ece4cf] bg-[#fbf7ea]"
              >
                <div className="font-mono text-xs text-[#7a5b0f] w-14">
                  {fmtDate(b.month, b.day)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#1c1a14] truncate">{b.name}</div>
                  {b.age_turning && (
                    <div className="text-[10px] text-[#8b7e5e]">
                      Cumple {b.age_turning} años
                    </div>
                  )}
                </div>
                <span
                  className={`text-[10px] uppercase tracking-widest px-2 py-1 ${relClass(
                    b.days_until,
                  )}`}
                >
                  {relLabel(b.days_until)}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Anniversaries */}
        <div>
          <div className="flex items-center gap-2 mb-3 text-[#1c2a3a]">
            <Heart size={16} className="text-[#c8a04a]" />
            <span className="text-xs uppercase tracking-widest font-semibold">
              Aniversarios en la iglesia
            </span>
            <span className="ml-auto text-xs text-[#8b7e5e]">
              {anniversaries.length}
            </span>
          </div>
          <ul
            data-testid={DASHBOARD.upcomingAnniversariesList}
            className="space-y-1 max-h-64 overflow-y-auto pr-1"
          >
            {anniversaries.length === 0 && !loading && (
              <li className="text-xs text-[#8b7e5e] italic py-2">
                Sin aniversarios próximos
              </li>
            )}
            {anniversaries.map((a) => (
              <li
                key={`a-${a.id}`}
                data-testid={DASHBOARD.upcomingAnniversaryItem(a.id)}
                className="flex items-center gap-3 px-3 py-2 border border-[#ece4cf] bg-white"
              >
                <div className="font-mono text-xs text-[#1c2a3a] w-14">
                  {fmtDate(a.month, a.day)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#1c1a14] truncate">{a.name}</div>
                  <div className="text-[10px] text-[#8b7e5e]">
                    Cumple {a.years} {a.years === 1 ? "año" : "años"} en la iglesia
                  </div>
                </div>
                <span
                  className={`text-[10px] uppercase tracking-widest px-2 py-1 ${relClass(
                    a.days_until,
                  )}`}
                >
                  {relLabel(a.days_until)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
